import {
  GoogleMap,
  Polygon,
  DrawingManager,
  Autocomplete,
} from "@react-google-maps/api";
import React, { useState, useRef, useEffect, useMemo } from "react";
import "./geomap.css";
import { useGoogleMaps } from "../../../../../../../../../utils/mapConfig";
import { Col, Row } from "react-bootstrap";
import { BiSolidPolygon } from "react-icons/bi";
import { Button } from "@mui/material";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import WrongLocationIcon from "@mui/icons-material/WrongLocation";
import SaveIcon from "@mui/icons-material/Save";

const GeoMap = ({
  location,
  editable,
  onEditToggle,
  setPolygonPoints,
  selectedMap,
  setSelectedMap,
  editing,
  isFullScreen
}) => {
  const [polygonPath, setPolygonPath] = useState(location || []);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [drawingMode, setDrawingMode] = useState(null);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  const { isLoaded } = useGoogleMaps();

  const polygonRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const mapRef = useRef(null);

  const mapCenter = useMemo(() => {
    if (location?.length > 0) {
      return location?.length > 0 ? location[0] : { lat: 0, lng: 0 };
    } else {
      return currentLocation;
    }
  }, [location, currentLocation]);

  useEffect(() => {
    if (location?.length > 0) {
      setCurrentLocation(location[0]);
    }
  }, [location]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCurrentLocation({ lat: latitude, lng: longitude });
        },
        (error) => console.error("Error fetching current location:", error)
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  }, []);

  useEffect(() => {
    setPolygonPath(location);
    setIsSubmitEnabled(false);
  }, [location]);

  const handlePolygonLoad = (polygon) => {
    polygonRef.current = polygon;
  };

  const handlePolygonComplete = (polygon) => {
    const path = polygon
      .getPath()
      .getArray()
      .map((coord) => ({ lat: coord.lat(), lng: coord.lng() }));

    if (path.length > 0 && !arePointsEqual(path[0], path[path.length - 1])) {
      path.push(path[0]);
    }

    setPolygonPath(path);
    setIsSubmitEnabled(true);

    polygon.setMap(null);
    setDrawingMode(null);
  };

  const arePointsEqual = (point1, point2) => {
    return point1.lat === point2.lat && point1.lng === point2.lng;
  };

  const handlePolygonEdit = () => {
    if (polygonRef.current) {
      const updatedPath = polygonRef.current
        .getPath()
        .getArray()
        .map((coord) => ({ lat: coord.lat(), lng: coord.lng() }));

      if (
        updatedPath.length > 0 &&
        updatedPath[0] !== updatedPath[updatedPath.length - 1]
      ) {
        updatedPath.push(updatedPath[0]);
      }

      setPolygonPath(updatedPath);
      setIsSubmitEnabled(true);
    }
  };

  const handleSubmit = () => {
    if (polygonRef.current) {
      const updatedPath = polygonRef.current
        .getPath()
        .getArray()
        .map((coord) => ({ lat: coord.lat(), lng: coord.lng() }));
      setPolygonPath(updatedPath);
      setPolygonPoints(updatedPath);
    }

    setIsSubmitEnabled(false);
    editable && onEditToggle();
  };

  const handleClear = () => {
    setPolygonPath([]);
    setIsSubmitEnabled(false);
  };

  const onAutocompleteLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const handlePlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setCurrentLocation({ lat, lng });
        if (mapRef.current) {
          mapRef.current.panTo({ lat, lng });
        }
      } else {
        alert("The selected location does not have a valid geometry.");
      }
    }
  };

  useEffect(() => {
    let originalParent;

    const handleFullscreenChange = (event) => {
      const target = event.target;
      const mapControls = document.querySelector(".map-controls-inside-mapss");

      if (mapControls) {
        if (document.fullscreenElement === target) {
          originalParent = mapControls.parentElement;
          if (selectedMap === 2 && !editing) {
            target.appendChild(mapControls);
          } else {
            if (target.contains(mapControls)) {
              target.removeChild(mapControls);
            }
          }
        } else if (originalParent) {
          originalParent.appendChild(mapControls);
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [selectedMap, editing]);

  const handleDrawToggle = () => {
    editable && onEditToggle();

    if (drawingManagerRef.current) {
      drawingManagerRef.current.setMap(null);
    }

    if (drawingMode === "polygon") {
      setDrawingMode(null);
    } else {
      setDrawingMode("polygon");
    }
  };

  const handleDrawingManagerLoad = (drawingManager) => {
    drawingManagerRef.current = drawingManager;
  };

  const options = {
    streetViewControl: false,
    fullscreenControl: false,
  };

  return isLoaded ? (
    <div onClick={() => setSelectedMap(2)} className="map-container">
      <GoogleMap
      mapContainerClassName={`appmap ${isFullScreen ? "fullscreen" : ""}`}
        center={mapCenter}
        zoom={16}
        fullscreenControll={false}
        options={options}
        onLoad={(map) => (mapRef.current = map)}
      >
        <div className="map-controls-inside-mapss">
          <Row className="align-items-center map-btn-controls ">
            <Col className="d-flex justify-content-center">
              <Autocomplete
                onLoad={onAutocompleteLoad}
                onPlaceChanged={handlePlaceChanged}
                className="g-map-autocomplete"
              >
                <input
                  type="text"
                  placeholder="Search service location"
                  className="autocomplete-input"
                  style={{
                    width: "300px",
                    padding: "10px",
                    margin: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                  }}
                  autoFocus={true}
                />
              </Autocomplete>
            </Col>
            <Col>
              <div className="map-control-btnss">
                <Button
                  className="map-submit-btn"
                  variant="contained"
                  color="primary"
                  onClick={handleDrawToggle}
                >
                  {drawingMode === "polygon" ? "Back" : <BiSolidPolygon />}
                </Button>
                {polygonPath?.length > 0 && (
                  <Button
                    className="map-submit-btn"
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      onEditToggle();
                      if (drawingMode === "polygon") {
                        handleDrawToggle();
                      }
                    }}
                  >
                    {editable ? "Back" : <EditLocationAltIcon />}
                  </Button>
                )}

                <Button
                  className="map-submit-btn"
                  variant="contained"
                  color="error"
                  onClick={handleClear}
                >
                  <WrongLocationIcon />
                </Button>
                <Button
                  className="map-submit-btn"
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                  disabled={!isSubmitEnabled}
                >
                  <SaveIcon />
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {drawingMode === "polygon" && (
          <DrawingManager
            onLoad={handleDrawingManagerLoad}
            drawingMode={drawingMode}
            options={{
              drawingControlOptions: {
                position: window.google.maps.ControlPosition.TOP_RIGHT,
                drawingModes: ["polygon"],
              },
            }}
            onPolygonComplete={handlePolygonComplete}
          />
        )}
        {polygonPath?.length > 0 && (
          <Polygon
            key={JSON.stringify(polygonPath)}
            path={polygonPath}
            options={{
              strokeColor: "#FF0000",
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: "#FF0000",
              fillOpacity: 0.35,
              editable: editable,
              draggable: editable,
            }}
            onLoad={handlePolygonLoad}
            onMouseUp={handlePolygonEdit}
          />
        )}
      </GoogleMap>
    </div>
  ) : (
    <div>Loading Map...</div>
  );
};

export default GeoMap;
